import { Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import WSDataGrid, { IColumns } from "../../../Components/DataGrid/WSDataGrid";
import { useNavigate } from "react-router-dom";
import { updateMainHeader, callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { useDispatch } from "react-redux";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import { useForm } from "react-hook-form";
import { UpdateStatus } from "../../Reports/subComponents/UpdateStatus";

export const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showMessage } = useSnackbar();

  // ✅ State
  const [isStatusPopupOpen, setStatusPopupOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ✅ Form
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      newStatus: "",
    },
  });

  useEffect(() => {
    updateMainHeader("Sales");
  }, []);

  // ✅ Breadcrumbs
  breadCrumbsStore.updateBreadCrumbs([
    { "Invoice Management": "/Invoice/list" },
  ]);

  // ✅ Status Options
  const statusOptions = [
    { value: "Draft", label: "Draft" },
    { value: "Confirmed", label: "Confirmed" },
    { value: "Cancelled", label: "Cancelled" },
  ];

  // ✅ API Source
  const source = {
    url: "invoice/list",
    page: 1,
    pageSize: 10,
    filter: [],
    sort: [],
    type: "GET" as const,
  };

  // ✅ Handle Update Status API
  const handleUpdateStatus = async (data: any) => {
    try {
      if (!selectedInvoiceId) return;

      const body = {
        Status: data.newStatus,
      };

      const response = await callAPI(
        `invoice/status/${selectedInvoiceId}`,
        Method.PUT,
        body
      );

      if (response?.success) {
        showMessage("Invoice status updated successfully!", "success");
        setStatusPopupOpen(false);
        reset();

        // 🔄 Refresh grid
        setRefreshKey((prev) => prev + 1);
      } else {
        showMessage(response?.message || "Failed to update status", "error");
      }
    } catch (error: any) {
      console.error("❌ Update Status Error:", error);
      showMessage(error?.message || "Something went wrong", "error");
    }
  };

  // ✅ Columns
  const columns: IColumns[] = [
    { header: "Date", fieldName: "CreatedDate", type: "date" as const },
    { header: "Invoice No", fieldName: "InvoiceNumber" },
    { header: "Customer", fieldName: "CustomerName" },
    { header: "Total Amount", fieldName: "TotalAmount", type: "amount" },
    { header: "Status", fieldName: "Status", type: "status" as const },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action" as const,
      actions: ["Print", "Update Status"],
      actionCallback: (action: string, row: any) => {
        const InvoiceId = row.InvoiceId;

        if (!InvoiceId) {
          console.error("❌ No valid ID found in row:", row);
          showMessage("Invalid invoice data - missing ID", "error");
          return;
        }

        if (action === "Print") {
          window.open(`/invoice/print/${InvoiceId}`, "_blank", "noopener,noreferrer");
        } else if (action === "Update Status") {
          setSelectedInvoiceId(InvoiceId);
          setStatusPopupOpen(true);
        }
      },
    },
  ];

  // ✅ Add Button
  const handleClick = () => {
    navigate("/invoice");
  };

  return (
    <>
      <Grid p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <WSDataGrid
              key={refreshKey}
              id="invoiceGrid"
              title="Invoice Management"
              headerType="modern"
              columns={columns}
              source={source}
              exportCsv
              rowsPerPageCount={10}
              searchBox={{ fieldName: "InvoiceNumber" }}
              addButton={{ label: "Add New Invoice", color: "secondary" }}
              onAddButtonClick={handleClick}
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* ✅ Update Status Dialog */}
      <Dialog open={isStatusPopupOpen} onClose={() => setStatusPopupOpen(false)}>
        <DialogTitle>Update Invoice Status</DialogTitle>

        <DialogContent>
          <UpdateStatus
            control={control}
            errors={{}}
            register={register}
            id={selectedInvoiceId}
            reset={reset}
            config={statusOptions}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setStatusPopupOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleUpdateStatus)}
            disabled={!isValid}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};